import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, MessageCircle } from 'lucide-react';

// Bold editorial quiz questions
const quizQuestions = [
  {
    id: 1,
    key: 'current_chapter',
    eyebrow: 'LET\'S START HERE',
    question: "What chapter are you in?",
    options: [
      "I'm Starting Over",
      "I'm Creating More Freedom",
      "I'm Building My First Online Income",
      "I'm Growing My Business",
      "I'm Ready to Scale"
    ]
  },
  {
    id: 2,
    key: 'twelve_month_goal',
    eyebrow: 'DREAM BIG',
    question: "12 months from now, what changes everything?",
    options: [
      "I'd earn my first income online.",
      "I'd replace my full-time income.",
      "I'd create another income stream.",
      "I'd build a personal brand I'm proud of.",
      "I'd grow the business I've already built."
    ]
  },
  {
    id: 3,
    key: 'experience_level',
    eyebrow: 'BE HONEST',
    question: "Where are you really at?",
    options: [
      "Complete beginner.",
      "I've tried before but never gained momentum.",
      "I've made a few sales.",
      "I'm earning online and want to grow.",
      "I'm ready to scale."
    ]
  },
  {
    id: 4,
    key: 'biggest_obstacle',
    eyebrow: 'THE REAL TALK',
    question: "What's actually holding you back?",
    options: [
      "I don't know where to begin.",
      "I feel overwhelmed by all the options.",
      "I don't have enough time.",
      "I struggle with consistency.",
      "I need better systems and automation.",
      "I need accountability and support."
    ]
  },
  {
    id: 5,
    key: 'support_type',
    eyebrow: 'YOUR STYLE',
    question: "How do you want to be supported?",
    options: [
      "Self-paced learning I can do on my own time.",
      "Templates and frameworks I can implement.",
      "A community of like-minded women.",
      "One-on-one mentorship and guidance.",
      "Done-with-you support and systems."
    ]
  },
  {
    id: 6,
    key: 'building_goal',
    eyebrow: 'THE VISION',
    question: "What are you building?",
    options: [
      "A digital product (course, template, guide).",
      "A coaching or service business.",
      "A content-based brand (social media, blog).",
      "Multiple income streams.",
      "I'm not sure yet — I need clarity."
    ]
  }
];

// Bold accent color - darker deep red
const ACCENT = '#8B0000';
// Blush pink for typography
const BLUSH = '#FFD4D2';

const EditorialHomePage = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [splashPhase, setSplashPhase] = useState(0);
  const [currentView, setCurrentView] = useState('home');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [chapterResult, setChapterResult] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [hoveredOption, setHoveredOption] = useState(null);

  useEffect(() => {
    const timer1 = setTimeout(() => setSplashPhase(1), 300);
    const timer2 = setTimeout(() => setSplashPhase(2), 2500);
    const timer3 = setTimeout(() => setShowSplash(false), 3300);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const handleStartQuiz = () => {
    setCurrentView('quiz');
    setCurrentQuestion(0);
  };

  const handleAnswer = (answer) => {
    const question = quizQuestions[currentQuestion];
    setAnswers(prev => ({
      ...prev,
      [question.key]: answer
    }));
    
    if (currentQuestion < quizQuestions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      setTimeout(() => setCurrentView('email'), 300);
    }
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chapter/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          email: email,
          quiz_answers: answers
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setChapterResult(data);
        setLeadId(data.id);
        setCurrentView('result');
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
    }
    setIsSubmitting(false);
  };

  const trackAction = async (action) => {
    if (!leadId) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chapter/track/${leadId}?action=${action}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error tracking action:', error);
    }
  };

  // ============================================
  // SPLASH SCREEN - Simple Fade In/Out
  // ============================================
  if (showSplash) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center z-50"
        style={{ 
          backgroundColor: '#000000',
          opacity: splashPhase === 2 ? 0 : 1,
          transition: 'opacity 0.8s ease-out'
        }}
      >
        <div className="w-full max-w-3xl px-8">
          <img 
            src="/audacity.png" 
            alt="a little audacity looks good on you."
            className="w-full h-auto"
            style={{
              opacity: splashPhase >= 1 ? 1 : 0,
              transition: 'opacity 1s ease-in'
            }}
          />
        </div>
      </div>
    );
  }

  // ============================================
  // QUIZ VIEW - Dark & Bold
  // ============================================
  if (currentView === 'quiz') {
    const question = quizQuestions[currentQuestion];
    const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
    
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        {/* Progress Bar */}
        <div className="w-full h-1" style={{ backgroundColor: '#1a1a1a' }}>
          <div 
            className="h-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%`, backgroundColor: ACCENT }}
          />
        </div>
        
        {/* Question Counter */}
        <div className="pt-8 px-8 text-center">
          <span 
            className="text-xs tracking-[0.3em]"
            style={{ color: 'rgba(255, 212, 210, 0.4)' }}
          >
            {String(currentQuestion + 1).padStart(2, '0')} / {String(quizQuestions.length).padStart(2, '0')}
          </span>
        </div>
        
        {/* Question */}
        <div className="flex-1 flex items-center justify-center px-8 py-8">
          <div 
            className="w-full max-w-2xl"
            style={{ animation: 'fadeInUp 0.5s ease-out' }}
          >
            {/* Eyebrow */}
            <p 
              className="text-xs tracking-[0.4em] mb-6 text-center"
              style={{ color: ACCENT }}
            >
              {question.eyebrow}
            </p>
            
            {/* Question Text */}
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-light text-center mb-12"
              style={{ 
                fontFamily: "'Playfair Display', serif", 
                color: BLUSH,
                lineHeight: '1.2'
              }}
            >
              {question.question}
            </h2>
            
            {/* Options */}
            <div className="space-y-3">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option)}
                  onMouseEnter={() => setHoveredOption(index)}
                  onMouseLeave={() => setHoveredOption(null)}
                  className="w-full p-5 text-left transition-all duration-300"
                  style={{ 
                    backgroundColor: hoveredOption === index ? '#1a1a1a' : 'transparent',
                    border: `1px solid ${hoveredOption === index ? ACCENT : '#2a2a2a'}`,
                    color: hoveredOption === index ? BLUSH : 'rgba(255, 212, 210, 0.7)',
                    transform: hoveredOption === index ? 'translateX(8px)' : 'translateX(0)'
                  }}
                  data-testid={`quiz-option-${index}`}
                >
                  <span className="text-base font-light">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ============================================
  // EMAIL CAPTURE - Bold Split Screen
  // ============================================
  if (currentView === 'email') {
    return (
      <div 
        className="min-h-screen relative overflow-hidden"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        {/* Large Background Text */}
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
          style={{ 
            fontSize: 'clamp(200px, 30vw, 500px)',
            fontFamily: "'Playfair Display', serif",
            color: 'rgba(255, 212, 210, 0.03)',
            fontWeight: '400',
            letterSpacing: '-0.02em',
            lineHeight: '0.8'
          }}
        >
          FU
        </div>

        {/* Desktop: Split Screen */}
        <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
          
          {/* Left Side - Bold Statement */}
          <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
            <div className="max-w-lg" style={{ animation: 'fadeInUp 0.8s ease-out' }}>
              {/* Eyebrow */}
              <p 
                className="text-xs tracking-[0.4em] mb-8"
                style={{ color: ACCENT }}
              >
                BREAK FREE & BUILD™
              </p>
              
              {/* Main Headline with strikethrough effect */}
              <h1 
                className="text-5xl md:text-6xl lg:text-7xl font-light mb-8 leading-none"
                style={{ fontFamily: "'Playfair Display', serif", color: BLUSH }}
              >
                <span style={{ 
                  textDecoration: 'line-through', 
                  textDecorationColor: ACCENT,
                  color: 'rgba(255, 212, 210, 0.4)'
                }}>WAITING</span>
                <br />
                <span style={{ color: ACCENT, fontStyle: 'italic' }}>START</span>
                <br />
                BUILDING.
              </h1>
              
              {/* Line accent */}
              <div 
                className="w-16 h-0.5 mb-8"
                style={{ backgroundColor: ACCENT }}
              />
              
              {/* Supporting Copy */}
              <p 
                className="text-base font-light leading-relaxed"
                style={{ color: 'rgba(255, 212, 210, 0.6)' }}
              >
                You answered the questions.
                <br />
                Now let's get you your roadmap—
                <br />
                <span style={{ color: BLUSH }}>and your next move.</span>
              </p>
            </div>
          </div>

          {/* Right Side - Form on Dark */}
          <div 
            className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16"
            style={{ backgroundColor: '#111111' }}
          >
            <div 
              className="w-full max-w-md"
              style={{ animation: 'fadeInUp 0.8s ease-out 0.2s both' }}
            >
              {/* Form Header */}
              <p 
                className="text-xs tracking-[0.3em] mb-4"
                style={{ color: ACCENT }}
              >
                UNLOCK YOUR CHAPTER
              </p>
              
              <h2 
                className="text-2xl md:text-3xl font-light mb-2"
                style={{ fontFamily: "'Playfair Display', serif", color: BLUSH }}
              >
                Your roadmap is ready.
              </h2>
              
              <p 
                className="text-sm font-light mb-10"
                style={{ color: 'rgba(255, 212, 210, 0.5)' }}
              >
                Enter your details to see your personalized chapter + free training.
              </p>
              
              <form onSubmit={handleSubmitEmail} className="space-y-6">
                <div>
                  <input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="w-full px-0 py-4 text-base font-light bg-transparent border-0 border-b focus:outline-none transition-colors"
                    style={{ 
                      borderColor: '#2a2a2a',
                      color: BLUSH
                    }}
                    onFocus={(e) => e.target.style.borderColor = ACCENT}
                    onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
                    data-testid="email-first-name"
                  />
                </div>
                
                <div>
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-0 py-4 text-base font-light bg-transparent border-0 border-b focus:outline-none transition-colors"
                    style={{ 
                      borderColor: '#2a2a2a',
                      color: BLUSH
                    }}
                    onFocus={(e) => e.target.style.borderColor = ACCENT}
                    onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
                    data-testid="email-input"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 mt-4 text-sm tracking-[0.2em] font-medium transition-all duration-300 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-3"
                  style={{ 
                    backgroundColor: ACCENT,
                    color: '#ffffff'
                  }}
                  data-testid="unlock-roadmap-btn"
                >
                  {isSubmitting ? 'UNLOCKING...' : 'SHOW ME MY CHAPTER'}
                  {!isSubmitting && <ArrowRight size={16} strokeWidth={2} />}
                </button>
              </form>
              
              <p 
                className="mt-8 text-xs font-light text-center"
                style={{ color: 'rgba(255, 212, 210, 0.3)' }}
              >
                No spam. No fluff. Just clarity.
              </p>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ============================================
  // RESULTS PAGE - Bold Chapter Reveal
  // ============================================
  if (currentView === 'result' && chapterResult) {
    const chapterContent = {
      'foundation': {
        number: '01',
        title: 'THE FOUNDATION',
        subtitle: 'CLARITY FIRST',
        message: "You're not behind. You're at the beginning of something powerful. This chapter is about getting crystal clear on what you want to build—and taking your first real steps.",
        nextSteps: [
          "Get clear on your unique value",
          "Choose the right path for YOU",
          "Build your first offer with confidence"
        ],
        cta: 'WATCH FREE TRAINING',
        ctaAction: () => trackAction('watched_training')
      },
      'builder': {
        number: '02',
        title: 'THE BUILDER',
        subtitle: 'CREATE & LAUNCH',
        message: "You've got the vision. Now it's time to build. This chapter is about turning ideas into income—creating your offer, launching it, and making sales.",
        nextSteps: [
          "Create your signature digital product",
          "Build a launch strategy that converts",
          "Make your first (or next) sales"
        ],
        cta: 'EXPLORE THE ACADEMY',
        ctaAction: () => {
          trackAction('clicked_mentorship');
          window.open('https://stan.store/FarzeenDigitalWealth', '_blank');
        }
      },
      'growth': {
        number: '03',
        title: 'THE GROWTH',
        subtitle: 'EXPAND & OPTIMIZE',
        message: "You've started. You've made sales. Now it's time to grow. This chapter is about scaling your brand, optimizing what works, and building multiple revenue streams.",
        nextSteps: [
          "Scale your content to reach more people",
          "Optimize your offers for higher conversions",
          "Add additional income streams"
        ],
        cta: 'BOOK A STRATEGY CALL',
        ctaAction: () => {
          trackAction('booked_call');
          window.open('https://calendly.com/farzeen-farzeenkhan/30min', '_blank');
        }
      },
      'freedom': {
        number: '04',
        title: 'THE FREEDOM',
        subtitle: 'SYSTEMS & SCALE',
        message: "You're ready for true freedom. This chapter is about building systems that work without you—automation, delegation, and income that doesn't depend on your time.",
        nextSteps: [
          "Build systems that run without you",
          "Automate your marketing and sales",
          "Create passive income streams"
        ],
        cta: 'EXPLORE VIP SYSTEMS',
        ctaAction: () => {
          trackAction('clicked_vip_systems');
          window.open('https://stan.store/FarzeenDigitalWealth', '_blank');
        }
      }
    };

    const chapter = chapterContent[chapterResult.assigned_chapter] || chapterContent['foundation'];

    return (
      <div 
        className="min-h-screen relative overflow-hidden"
        style={{ backgroundColor: '#0a0a0a' }}
      >
        {/* Large Background Number */}
        <div 
          className="absolute top-0 left-0 pointer-events-none select-none"
          style={{ 
            fontSize: 'clamp(300px, 50vw, 600px)',
            fontFamily: "'Playfair Display', serif",
            color: 'rgba(255, 212, 210, 0.03)',
            fontWeight: '400',
            lineHeight: '0.8',
            transform: 'translate(-10%, -20%)'
          }}
        >
          {chapter.number}
        </div>

        <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
          {/* Left Side - Chapter Reveal */}
          <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
            <div 
              className="max-w-lg"
              style={{ animation: 'fadeInUp 0.8s ease-out' }}
            >
              <p 
                className="text-xs tracking-[0.4em] mb-8"
                style={{ color: 'rgba(255, 212, 210, 0.5)' }}
              >
                YOUR CHAPTER
              </p>
              
              <h1 
                className="text-5xl md:text-6xl lg:text-7xl font-light mb-4 leading-none"
                style={{ fontFamily: "'Playfair Display', serif", color: BLUSH }}
              >
                {chapter.title.split(' ')[0]}
                <br />
                <span style={{ color: ACCENT, fontStyle: 'italic' }}>
                  {chapter.title.split(' ').slice(1).join(' ')}
                </span>
              </h1>
              
              <p 
                className="text-xs tracking-[0.3em] mb-8"
                style={{ color: ACCENT }}
              >
                {chapter.subtitle}
              </p>
              
              <p 
                className="text-base font-light leading-relaxed mb-10"
                style={{ color: 'rgba(255, 212, 210, 0.7)' }}
              >
                {chapter.message}
              </p>
              
              {/* Next Steps */}
              <div className="space-y-4">
                {chapter.nextSteps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div 
                      className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: ACCENT }}
                    >
                      <Check size={14} color="#ffffff" strokeWidth={3} />
                    </div>
                    <span 
                      className="text-sm font-light" 
                      style={{ color: 'rgba(255, 212, 210, 0.8)' }}
                    >
                      {step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - CTAs */}
          <div 
            className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16"
            style={{ backgroundColor: '#111111' }}
          >
            <div 
              className="w-full max-w-md text-center"
              style={{ animation: 'fadeInUp 0.8s ease-out 0.2s both' }}
            >
              <p 
                className="text-xs tracking-[0.3em] mb-4"
                style={{ color: ACCENT }}
              >
                YOUR NEXT MOVE
              </p>
              
              <h2 
                className="text-3xl md:text-4xl font-light mb-4"
                style={{ fontFamily: "'Playfair Display', serif", color: BLUSH }}
              >
                Ready, {firstName}?
              </h2>
              
              <p 
                className="text-sm font-light mb-10"
                style={{ color: 'rgba(255, 212, 210, 0.5)' }}
              >
                Choose your path forward.
              </p>
              
              <div className="space-y-4">
                {/* Primary CTA */}
                <button
                  onClick={chapter.ctaAction}
                  className="w-full py-5 text-sm tracking-[0.2em] font-medium transition-all duration-300 hover:opacity-90 flex items-center justify-center gap-3"
                  style={{ backgroundColor: ACCENT, color: '#ffffff' }}
                  data-testid="chapter-primary-cta"
                >
                  {chapter.cta}
                  <ArrowRight size={16} strokeWidth={2} />
                </button>
                
                {/* Book a Call */}
                <a
                  href="https://calendly.com/farzeen-farzeenkhan/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackAction('booked_call')}
                  className="w-full py-5 text-sm tracking-[0.15em] font-light transition-all duration-300 hover:border-opacity-60 flex items-center justify-center gap-3 border block"
                  style={{ 
                    backgroundColor: 'transparent',
                    color: BLUSH,
                    borderColor: '#2a2a2a'
                  }}
                  data-testid="book-call-btn"
                >
                  BOOK A CLARITY CALL
                  <ArrowRight size={16} strokeWidth={1.5} />
                </a>
                
                {/* Training Link */}
                <button
                  onClick={() => trackAction('watched_training')}
                  className="text-sm font-light underline transition-opacity hover:opacity-60 mt-6"
                  style={{ color: 'rgba(255, 212, 210, 0.5)' }}
                  data-testid="watch-training-link"
                >
                  Watch Free Training First
                </button>
              </div>
              
              {/* Instagram DM */}
              <div className="mt-16 pt-8 border-t" style={{ borderColor: '#2a2a2a' }}>
                <a 
                  href="https://ig.me/m/itsfarzeenkhan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 hover:opacity-80"
                  style={{ backgroundColor: ACCENT }}
                  data-testid="instagram-dm-btn"
                >
                  <MessageCircle size={20} color="#ffffff" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ============================================
  // HOME VIEW - Bold Editorial
  // ============================================
  return (
    <div 
      className="min-h-screen flex items-center justify-center px-8 relative overflow-hidden"
      style={{ 
        backgroundColor: '#0a0a0a',
        animation: 'fadeIn 1s ease-out'
      }}
    >
      {/* Import handwritten font */}
      <link href="https://fonts.googleapis.com/css2?family=Allura&display=swap" rel="stylesheet" />
      
      {/* Large Diagonal Signature - Bottom Right Background */}
      <div 
        className="absolute pointer-events-none"
        style={{ 
          bottom: '-15%',
          right: '-8%',
          transform: 'rotate(-5deg)',
          animation: 'fadeIn 2s ease-out 1.5s both'
        }}
      >
        <img 
          src="/signature.png" 
          alt="" 
          className="w-[500px] md:w-[700px] lg:w-[900px]"
          style={{ 
            filter: 'brightness(1.5)',
            mixBlendMode: 'screen',
            opacity: 0.15
          }}
        />
      </div>

      <div className="w-full max-w-3xl text-center relative z-10">
        {/* Brand Identifier */}
        <p 
          className="text-xs tracking-[0.3em] mb-16"
          style={{ color: ACCENT }}
        >
          BREAK FREE & BUILD™
        </p>
        
        {/* Main Headline - FU MONEY */}
        <h1 
          className="text-6xl md:text-7xl lg:text-8xl font-light mb-6 leading-none"
          style={{ fontFamily: "'Playfair Display', serif" }}
          data-testid="home-headline"
        >
          <span style={{ color: ACCENT, fontStyle: 'italic' }}>FU</span>
          <span style={{ color: BLUSH }}> MONEY.</span>
        </h1>
        
        {/* Meaning Reveal */}
        <div 
          className="mb-12 space-y-2"
          style={{ animation: 'fadeInUp 1s ease-out 0.5s both' }}
        >
          <p 
            className="text-sm md:text-base tracking-[0.25em] font-light"
            style={{ color: 'rgba(255, 212, 210, 0.7)' }}
          >
            FINANCIALLY UNAPOLOGETIC.
          </p>
          <p 
            className="text-sm md:text-base tracking-[0.25em] font-light"
            style={{ 
              color: 'rgba(255, 212, 210, 0.7)',
              animation: 'fadeInUp 1s ease-out 0.8s both'
            }}
          >
            FREEDOM UNLOCKED.
          </p>
        </div>
        
        {/* Supporting Copy */}
        <div 
          className="mb-12"
          style={{ animation: 'fadeInUp 1s ease-out 1.1s both' }}
        >
          <p 
            className="text-base md:text-lg font-light leading-relaxed"
            style={{ color: 'rgba(255, 212, 210, 0.6)' }}
          >
            Build your brand. Build your income.
          </p>
          <p 
            className="text-base md:text-lg font-light"
            style={{ color: BLUSH }}
          >
            Build on your terms.
          </p>
        </div>
        
        {/* CTA Button */}
        <div style={{ animation: 'fadeInUp 1s ease-out 1.4s both' }}>
          <button
            onClick={handleStartQuiz}
            className="inline-flex items-center gap-3 px-10 py-5 text-sm tracking-[0.2em] font-medium transition-all duration-300 hover:opacity-90"
            style={{ 
              backgroundColor: ACCENT,
              color: '#ffffff'
            }}
            data-testid="start-chapter-btn"
          >
            START YOUR CHAPTER
            <ArrowRight size={18} strokeWidth={2} />
          </button>
          
          <p 
            className="mt-6 text-sm font-light"
            style={{ color: 'rgba(255, 212, 210, 0.4)' }}
          >
            60 seconds. Your personalized roadmap.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
    </div>
  );
};

export default EditorialHomePage;
